import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Icon,
  Button,
  Modal,
  Progress,
  Segment,
  Message,
} from "semantic-ui-react";
import { Cropper } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import { profileApi, ProfileApiError } from "@api/profile/profile";
import { getFullImageUrl, useIsMobile } from "@utils/mobileUtils";

const COLORS = {
  primary: "#225745",
  secondary: "#f4b952",
  white: "#fff",
  textSecondary: "#666",
};

const STORAGE_KEY = "profile_image_url";

// Minimum time (ms) to show the uploading UI so the animation is visible
const MIN_UPLOAD_DISPLAY_MS = 2500;

/**
 * ProfileAvatar - Handles user profile image display, upload, cropping, and deletion
 * Features: Image cropping, progress tracking, localStorage persistence, mobile responsive
 */
const ProfileAvatar = ({ user, onImageUpdate, onError }) => {
  const [state, setState] = useState({
    isHovered: false,
    showModal: false,
    selectedFile: null,
    previewUrl: null,
    uploadError: null,
    deleteError: null,
    isUploading: false,
    uploadProgress: 0,
    currentImageUrl: user?.image_url || null,
    showCropper: false,
  });

  const fileInputRef = useRef(null);
  const cropperRef = useRef(null);
  const uploadStartRef = useRef(null);
  const isMobile = useIsMobile();

  // Inject spinner keyframes once
  useEffect(() => {
    const styleId = "profile-avatar-spinner-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        @keyframes pd-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .pd-spinner { animation: pd-spin 1.35s linear infinite; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Sync with localStorage
  useEffect(() => {
    const savedImage = localStorage.getItem(STORAGE_KEY);
    if (savedImage && !state.currentImageUrl) {
      setState((prev) => ({ ...prev, currentImageUrl: savedImage }));
    }
  }, []);

  useEffect(() => {
    if (state.currentImageUrl) {
      localStorage.setItem(STORAGE_KEY, state.currentImageUrl);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.currentImageUrl]);

  useEffect(() => {
    if (
      user?.image_url !== undefined &&
      user.image_url !== state.currentImageUrl
    ) {
      setState((prev) => ({ ...prev, currentImageUrl: user.image_url }));
    }
  }, [user?.image_url, state.currentImageUrl]);

  const updateState = (updates) =>
    setState((prev) => ({ ...prev, ...updates }));

  const resetModalState = () => {
    updateState({
      selectedFile: null,
      previewUrl: null,
      uploadError: null,
      deleteError: null,
      uploadProgress: 0,
      showCropper: false,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = profileApi.validateImageFile(file);
    if (!validation.valid) {
      updateState({ uploadError: validation.errors.join("; ") });
      return;
    }

    updateState({ selectedFile: file, uploadError: null });
    const reader = new FileReader();
    reader.onload = (e) =>
      updateState({ previewUrl: e.target.result, showCropper: true });
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async () => {
    const canvas = cropperRef.current?.getCanvas();
    if (!canvas) return;

    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          updateState({ uploadError: "Failed to process image" });
          return;
        }

        const croppedFile = new File([blob], state.selectedFile.name, {
          type: "image/jpeg",
        });
        updateState({ selectedFile: croppedFile, showCropper: false });
        await handleUpload(croppedFile);
      },
      "image/jpeg",
      0.85
    );
  };

  const wait = (ms) => new Promise((res) => setTimeout(res, ms));

  const handleUpload = async (fileToUpload = state.selectedFile) => {
    if (!fileToUpload) return;

    updateState({ isUploading: true, uploadError: null, uploadProgress: 0 });
    uploadStartRef.current = Date.now();

    let success = false;
    let response = null;

    try {
      response = await profileApi.uploadImage(fileToUpload, (progress) => {
        updateState({ uploadProgress: progress });
      });

      if (response?.success) {
        success = true;
        updateState({ currentImageUrl: response.image_url });
        if (onImageUpdate) onImageUpdate(response.image_url);
      } else {
        throw new Error(response?.error || "Upload failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof ProfileApiError
          ? error.message
          : "Upload failed. Please try again.";
      updateState({ uploadError: errorMessage });
      if (error instanceof ProfileApiError && onError) {
        onError({ type: "upload", code: error.code, message: error.message });
      }
    } finally {
      // Ensure spinner remains visible for minimum duration
      const elapsed = Date.now() - (uploadStartRef.current || 0);
      const remaining = Math.max(0, MIN_UPLOAD_DISPLAY_MS - elapsed);
      if (remaining > 0) await wait(remaining);

      updateState({ isUploading: false, uploadProgress: 0 });

      if (success) {
        updateState({ showModal: false });
        resetModalState();
      }
    }
  };

  const handleDelete = async () => {
    updateState({ isUploading: true, deleteError: null });
    uploadStartRef.current = Date.now();

    let success = false;
    let response = null;

    try {
      response = await profileApi.deleteImage();
      if (response?.success) {
        success = true;
        updateState({ currentImageUrl: null });
        if (onImageUpdate) onImageUpdate(null);
      } else {
        throw new Error(response?.error || "Delete failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof ProfileApiError
          ? error.message
          : "Delete failed. Please try again.";
      updateState({ deleteError: errorMessage });
      if (error instanceof ProfileApiError && onError) {
        onError({ type: "delete", code: error.code, message: error.message });
      }
    } finally {
      const elapsed = Date.now() - (uploadStartRef.current || 0);
      const remaining = Math.max(0, MIN_UPLOAD_DISPLAY_MS - elapsed);
      if (remaining > 0) await wait(remaining);

      updateState({ isUploading: false });

      if (success) {
        updateState({ showModal: false });
        resetModalState();
      }
    }
  };

  const avatarStyle = useMemo(
    () => ({
      width: isMobile ? "100px" : "120px",
      height: isMobile ? "100px" : "120px",
      borderRadius: "50%",
      backgroundColor: COLORS.secondary,
      margin: "0 auto 1rem auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `2px solid ${COLORS.secondary}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
    }),
    [isMobile]
  );

  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: state.isHovered ? 1 : 0,
    transition: "opacity 0.3s",
  };

  return (
    <div style={{ textAlign: "center", marginBottom: "2rem" }}>
      <div
        style={avatarStyle}
        onMouseEnter={() => updateState({ isHovered: true })}
        onMouseLeave={() => updateState({ isHovered: false })}
        onClick={() => {
          updateState({ showModal: true });
          resetModalState();
        }}
      >
        {state.currentImageUrl ? (
          <img
            src={getFullImageUrl(state.currentImageUrl)}
            alt="Profile"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => updateState({ currentImageUrl: null })}
          />
        ) : (
          <Icon
            name="user"
            size={isMobile ? "big" : "huge"}
            style={{ color: COLORS.white, margin: 0 }}
          />
        )}
        <div style={overlayStyle}>
          <Icon name="camera" size="large" style={{ color: COLORS.white }} />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          gap: isMobile ? "0.5rem" : "0",
        }}
      >
        <Button
          size={isMobile ? "tiny" : "small"}
          basic
          color="blue"
          onClick={() => updateState({ showModal: true })}
          loading={state.isUploading}
          disabled={state.isUploading}
          fluid={isMobile}
        >
          <Icon name="upload" />
          {state.currentImageUrl ? "Change Picture" : "Upload Picture"}
        </Button>

        {state.currentImageUrl && (
          <Button
            size={isMobile ? "tiny" : "small"}
            basic
            color="red"
            style={{ marginLeft: isMobile ? "0" : "0.5rem" }}
            onClick={handleDelete}
            loading={state.isUploading}
            disabled={state.isUploading}
            fluid={isMobile}
          >
            <Icon name="trash" />
            Delete
          </Button>
        )}
      </div>

      <Modal
        open={state.showModal}
        onClose={() => updateState({ showModal: false })}
        size="small"
        closeIcon
        style={{ borderRadius: "16px", overflow: "hidden" }}
      >
        <Modal.Header
          style={{
            padding: "1.5rem",
            fontSize: "1.3em",
            fontWeight: "600",
            borderBottom: "2px solid #f0f0f0",
          }}
        >
          <Icon name="camera" style={{ marginRight: "0.5rem" }} />
          {state.currentImageUrl
            ? "Change Profile Picture"
            : "Upload Profile Picture"}
        </Modal.Header>
        <Modal.Content style={{ padding: "2rem", backgroundColor: "#fafbfc" }}>
          {state.uploadError && (
            <Message
              negative
              style={{
                marginBottom: "1.5rem",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.1)",
              }}
            >
              <Message.Header>
                <Icon name="exclamation triangle" />
                Upload Error
              </Message.Header>
              <p>{state.uploadError}</p>
            </Message>
          )}

          {state.deleteError && (
            <Message
              negative
              style={{
                marginBottom: "1.5rem",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.1)",
              }}
            >
              <Message.Header>
                <Icon name="exclamation triangle" />
                Delete Error
              </Message.Header>
              <p>{state.deleteError}</p>
            </Message>
          )}

          {state.isUploading && (
            <Segment
              style={{
                marginBottom: "1.5rem",
                borderRadius: "16px",
                border: "2px solid #e8f4fd",
                backgroundColor: "#f5faff",
                padding: "1.5rem",
                boxShadow: "0 4px 12px rgba(33, 133, 208, 0.08)",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "1.2rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Spinner container with rotating border (icon stays static) */}
                <div
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: COLORS.white,
                    marginBottom: "0.8rem",
                    boxShadow: "0 4px 12px rgba(33, 133, 208, 0.15)",
                    position: "relative",
                  }}
                >
                  {/* Spinning border ring */}
                  <span
                    className="pd-spinner"
                    style={{
                      position: "absolute",
                      top: "0",
                      left: "0",
                      right: "0",
                      bottom: "0",
                      borderRadius: "50%",
                      border: "3px solid transparent",
                      borderTopColor: "#2185d0",
                      borderRightColor: "#2185d0",
                    }}
                  />
                  {/* Static icon */}
                  <Icon
                    name="cloud upload"
                    size="big"
                    color="blue"
                    style={{ margin: 0, position: "relative", zIndex: 1 }}
                  />
                </div>

                <p
                  style={{
                    fontSize: "1.1em",
                    fontWeight: "600",
                    color: "#2185d0",
                    marginBottom: "0.5rem",
                  }}
                >
                  {state.currentImageUrl
                    ? "Changing your image..."
                    : "Creating your profile..."}
                </p>
                <p
                  style={{
                    fontSize: "0.9em",
                    color: COLORS.textSecondary,
                  }}
                >
                  Please wait, this may take a few seconds.
                </p>
              </div>

              <Progress
                percent={state.uploadProgress}
                indicating
                progress
                color="blue"
                style={{ borderRadius: "12px", overflow: "hidden" }}
              />
            </Segment>
          )}

          <div style={{ textAlign: "center" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            {state.showCropper && state.previewUrl ? (
              <div>
                <div
                  style={{
                    height: "420px",
                    marginBottom: "1.5rem",
                    backgroundColor: COLORS.white,
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "3px solid #e8f4fd",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                    position: "relative",
                  }}
                >
                  <Cropper
                    ref={cropperRef}
                    src={state.previewUrl}
                    className="cropper"
                    stencilProps={{ aspectRatio: 1, grid: true, lines: true }}
                    style={{ height: "100%", background: "#ffffff" }}
                    backgroundWrapperProps={{
                      scaleImage: true,
                      moveImage: true,
                    }}
                  />
                </div>
                <div
                  style={{
                    marginBottom: "1.5rem",
                    padding: "1rem 1.25rem",
                    backgroundColor: "#f0f9ff",
                    borderRadius: "12px",
                    border: "2px solid #bfdbfe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Icon name="info circle" color="blue" style={{ margin: 0 }} />
                  <span
                    style={{
                      color: "#1e40af",
                      fontSize: "0.95em",
                      fontWeight: "500",
                    }}
                  >
                    Drag to reposition • Pinch or scroll to zoom • Adjust
                    corners to crop
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      borderRadius: "10px",
                      padding: "0.9rem 1.5rem",
                      fontWeight: "600",
                    }}
                  >
                    <Icon name="image" />
                    Choose Different
                  </Button>
                  <Button
                    primary
                    onClick={handleCropComplete}
                    disabled={state.isUploading}
                    loading={state.isUploading}
                    style={{
                      borderRadius: "10px",
                      padding: "0.9rem 2rem",
                      fontWeight: "600",
                    }}
                  >
                    <Icon name="check circle" />
                    Crop & Upload
                  </Button>
                </div>
              </div>
            ) : !state.selectedFile ? (
              <div
                style={{
                  padding: "2rem 1.5rem",
                  border: "2px dashed #c7e0f8",
                  borderRadius: "14px",
                  backgroundColor: COLORS.white,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  maxWidth: "420px",
                  margin: "0 auto",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                }}
                onClick={() => fileInputRef.current?.click()}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.08)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(0,0,0,0.04)")
                }
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "1rem",
                    backgroundColor: "#f0f8ff",
                    borderRadius: "50%",
                    marginBottom: "1rem",
                  }}
                >
                  <Icon
                    name="cloud upload"
                    size="large"
                    color="blue"
                    style={{ margin: 0 }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "1.1em",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0.4rem",
                  }}
                >
                  Upload or drag an image here
                </p>
                <p
                  style={{
                    fontSize: "0.9em",
                    color: COLORS.textSecondary,
                    marginBottom: "0.3rem",
                  }}
                >
                  Supports PNG, JPG, JPEG, GIF, WebP
                </p>
                <p
                  style={{
                    fontSize: "0.85em",
                    color: "#9ca3af",
                    marginBottom: "1rem",
                  }}
                >
                  Max size: 5MB
                </p>
                <Button
                  primary
                  size="medium"
                  style={{
                    borderRadius: "8px",
                    padding: "0.7rem 1.5rem",
                    fontWeight: "600",
                    fontSize: "0.9em",
                  }}
                >
                  <Icon name="folder open" />
                  Choose File
                </Button>
              </div>
            ) : null}
          </div>
        </Modal.Content>
        <Modal.Actions
          style={{
            backgroundColor: "#fafbfc",
            borderTop: "1px solid #e5e7eb",
            padding: "1.25rem 2rem",
          }}
        >
          <Button
            onClick={() => updateState({ showModal: false })}
            disabled={state.isUploading}
            style={{
              borderRadius: "10px",
              padding: "0.75rem 1.5rem",
              fontWeight: "600",
            }}
          >
            <Icon name="close" />
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

ProfileAvatar.propTypes = {
  user: PropTypes.object,
  onImageUpdate: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

export default ProfileAvatar;
